import mutations from './mutations';
import axios from 'axios';
import { authHeader } from '@/utility/globalFunctions';

const baseUrl = process.env.VUE_APP_BASE_URL;

const state = () => ({
  coupons: [],
  coupon: null,
  usageHistory: [],
  isLoading: false,
  isCouponLoading: false,
  isUsageHistoryLoading: false,
  error: null,
  totalCoupons: 0,
  searchResults: []
});

const actions = {
  async getAllCoupons({ commit }) {
    commit('GET_COUPONS_BEGIN');
    try {
      const response = await axios.get(`${baseUrl}/coupon/read/all`);
      if (response.status === 200) {
        commit('GET_COUPONS_SUCCESS', response.data.data);
      } else {
        commit('GET_COUPONS_ERR', response.data.data.message);
      }
    } catch (error) {
      commit('GET_COUPONS_ERR', error.message);
    }
  },

  async getCouponById({ commit }, couponId) {
    commit('GET_COUPON_BEGIN');
    try {
      const response = await axios.get(`${baseUrl}/coupon/read?id=${couponId}`);
      if (response.status === 200) {
        commit('GET_COUPON_SUCCESS', response.data.data[0]);
      } else {
        commit('GET_COUPON_ERR', response.data.data.message);
      }
    } catch (error) {
      commit('GET_COUPON_ERR', error.message);
    }
  },

  async createCoupon({ commit, dispatch }, couponData) {
    commit('CREATE_COUPON_BEGIN');
    try {
      const response = await axios.post(`${baseUrl}/coupon/create`, couponData, {
        headers: authHeader()
      });
      if (response.status === 200) {
        commit('CREATE_COUPON_SUCCESS', response.data.data);
        // Refresh the coupon list
        await dispatch('getAllCoupons');
        return { success: true, message: response.data.data.message };
      } else {
        commit('CREATE_COUPON_ERR', response.data.data.message);
        return { success: false, message: response.data.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.data?.message || error.message;
      commit('CREATE_COUPON_ERR', errorMessage);
      return { success: false, message: errorMessage };
    }
  },

  async updateCoupon({ commit, dispatch }, couponData) {
    commit('UPDATE_COUPON_BEGIN');
    try {
      const response = await axios.put(`${baseUrl}/coupon/update`, couponData, {
        headers: authHeader()
      });
      if (response.status === 200) {
        commit('UPDATE_COUPON_SUCCESS', response.data.data);
        // Refresh the coupon list
        await dispatch('getAllCoupons');
        return { success: true, message: response.data.data.message };
      } else {
        commit('UPDATE_COUPON_ERR', response.data.data.message);
        return { success: false, message: response.data.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.data?.message || error.message;
      commit('UPDATE_COUPON_ERR', errorMessage);
      return { success: false, message: errorMessage };
    }
  },

  async deleteCoupon({ commit, dispatch }, couponId) {
    commit('DELETE_COUPON_BEGIN');
    try {
      const response = await axios.delete(`${baseUrl}/coupon/delete`, {
        data: { id: couponId },
        headers: authHeader()
      });
      if (response.status === 200) {
        commit('DELETE_COUPON_SUCCESS', couponId);
        // Refresh the coupon list
        await dispatch('getAllCoupons');
        return { success: true, message: response.data.data.message };
      } else {
        commit('DELETE_COUPON_ERR', response.data.data.message);
        return { success: false, message: response.data.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.data?.message || error.message;
      commit('DELETE_COUPON_ERR', errorMessage);
      return { success: false, message: errorMessage };
    }
  },

  async searchCoupons({ commit }, searchQuery) {
    commit('SEARCH_COUPONS_BEGIN');
    try {
      const response = await axios.get(`${baseUrl}/coupon/search?query=${searchQuery}`);
      if (response.status === 200) {
        commit('SEARCH_COUPONS_SUCCESS', response.data.data);
      } else if (response.status === 202) {
        commit('SEARCH_COUPONS_SUCCESS', []);
      } else {
        commit('SEARCH_COUPONS_ERR', response.data.data.message);
      }
    } catch (error) {
      commit('SEARCH_COUPONS_ERR', error.message);
    }
  },

  async getCouponUsageHistory({ commit }, couponId) {
    commit('GET_USAGE_HISTORY_BEGIN');
    try {
      const response = await axios.get(`${baseUrl}/coupon/usage-history?couponId=${couponId}`, {
        headers: authHeader()
      });
      
      console.log('Vuex - Usage History Response:', response.data); // Debug log
      
      if (response.status === 200) {
        // Handle different response structures
        let usageData = [];
        
        if (response.data && response.data.data && response.data.data.rows && Array.isArray(response.data.data.rows)) {
          // New API structure: {data: {data: {rows: [...], total: X}}}
          usageData = response.data.data.rows;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          // Legacy structure: {data: {data: [...]}}
          usageData = response.data.data;
        } else if (response.data && response.data.data && response.data.data.message) {
          // Backend returns message instead of data
          usageData = [];
        } else if (Array.isArray(response.data)) {
          // Direct array: {data: [...]}
          usageData = response.data;
        }
        
        commit('GET_USAGE_HISTORY_SUCCESS', usageData);
      } else {
        commit('GET_USAGE_HISTORY_SUCCESS', []);
      }
    } catch (error) {
      commit('GET_USAGE_HISTORY_ERR', error.message);
    }
  },

  async validateCoupon({ commit }, { code, orderAmount, userId }) {
    commit('VALIDATE_COUPON_BEGIN');
    try {
      const response = await axios.post(`${baseUrl}/coupon/validate`, {
        code,
        orderAmount,
        userId
      });
      if (response.status === 200) {
        commit('VALIDATE_COUPON_SUCCESS', response.data.data);
        return { 
          success: true, 
          discount: response.data.data.discount,
          couponId: response.data.data.couponId 
        };
      } else {
        commit('VALIDATE_COUPON_ERR', response.data.data.message);
        return { success: false, message: response.data.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.data?.message || error.message;
      commit('VALIDATE_COUPON_ERR', errorMessage);
      return { success: false, message: errorMessage };
    }
  },

  async applyCoupon({ commit }, { couponId, userId, orderAmount }) {
    commit('APPLY_COUPON_BEGIN');
    try {
      const response = await axios.post(`${baseUrl}/coupon/apply`, {
        couponId,
        userId,
        orderAmount
      });
      if (response.status === 200) {
        commit('APPLY_COUPON_SUCCESS', response.data.data);
        return { success: true, message: response.data.data.message };
      } else {
        commit('APPLY_COUPON_ERR', response.data.data.message);
        return { success: false, message: response.data.data.message };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.data?.message || error.message;
      commit('APPLY_COUPON_ERR', errorMessage);
      return { success: false, message: errorMessage };
    }
  },

  // Filter coupons by status
  async filterCouponsByStatus({ commit, state }, status) {
    commit('FILTER_COUPONS_BEGIN');
    try {
      let filteredCoupons = [...state.coupons];
      
      if (status === 'active') {
        const currentTime = Math.floor(Date.now() / 1000);
        filteredCoupons = filteredCoupons.filter(coupon => 
          coupon.status === 1 && 
          (!coupon.expiry || coupon.expiry > currentTime)
        );
      } else if (status === 'inactive') {
        filteredCoupons = filteredCoupons.filter(coupon => coupon.status === 0);
      } else if (status === 'expired') {
        const currentTime = Math.floor(Date.now() / 1000);
        filteredCoupons = filteredCoupons.filter(coupon => 
          coupon.expiry && coupon.expiry <= currentTime
        );
      }
      
      commit('FILTER_COUPONS_SUCCESS', filteredCoupons);
    } catch (error) {
      commit('FILTER_COUPONS_ERR', error.message);
    }
  },

  // Clear search results
  clearSearch({ commit }) {
    commit('CLEAR_SEARCH');
  },

  // Clear errors
  clearError({ commit }) {
    commit('CLEAR_ERROR');
  }
};

const getters = {
  activeCoupons: (state) => {
    const currentTime = Math.floor(Date.now() / 1000);
    return state.coupons.filter(coupon => 
      coupon.status === 1 && 
      (!coupon.expiry || coupon.expiry > currentTime)
    );
  },
  
  inactiveCoupons: (state) => {
    return state.coupons.filter(coupon => coupon.status === 0);
  },
  
  expiredCoupons: (state) => {
    const currentTime = Math.floor(Date.now() / 1000);
    return state.coupons.filter(coupon => 
      coupon.expiry && coupon.expiry <= currentTime
    );
  },
  
  couponById: (state) => (id) => {
    return state.coupons.find(coupon => coupon.id === id);
  },
  
  couponByCode: (state) => (code) => {
    return state.coupons.find(coupon => coupon.code === code);
  }
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
